import React, { createContext, useContext, useState, useEffect } from 'react';

const FAQContext = createContext();

export const useFAQ = () => {
  const context = useContext(FAQContext);
  if (!context) {
    throw new Error('useFAQ must be used within a FAQProvider');
  }
  return context;
};

const defaultFAQs = [
  {
    id: 1,
    question: "Comment prendre rendez-vous chez un vétérinaire ?",
    answer: "C'est simple ! Utilisez notre barre de recherche pour trouver un vétérinaire près de chez vous. Sélectionnez la clinique de votre choix, choisissez un créneau disponible et confirmez votre rendez-vous. Vous recevrez une confirmation par email et SMS.",
    order: 1,
    active: true
  },
  {
    id: 2,
    question: "Le service est-il gratuit ?",
    answer: "Oui, vetoRDV est entièrement gratuit pour les patients ! Vous pouvez prendre autant de rendez-vous que vous le souhaitez sans aucun frais. Seule la consultation chez le vétérinaire est payante.",
    order: 2,
    active: true
  },
  {
    id: 3,
    question: "Comment annuler ou modifier un rendez-vous ?",
    answer: "Connectez-vous à votre espace patient, accédez à 'Mes rendez-vous' et cliquez sur le rendez-vous que vous souhaitez modifier ou annuler. Nous vous demandons d'annuler au moins 24h à l'avance par respect pour le vétérinaire.",
    order: 3,
    active: true
  },
  {
    id: 4,
    question: "Puis-je prendre rendez-vous pour plusieurs animaux ?",
    answer: "Oui, vous pouvez ajouter plusieurs animaux à votre profil et prendre des rendez-vous pour chacun d'eux. Lors de la prise de rendez-vous, sélectionnez simplement l'animal concerné.",
    order: 4,
    active: true
  },
  {
    id: 5,
    question: "Comment fonctionne le rappel de rendez-vous ?",
    answer: "Vous recevrez un rappel automatique par SMS et email la veille de votre rendez-vous. Assurez-vous que vos coordonnées sont bien à jour dans votre espace patient.",
    order: 5,
    active: true
  },
  {
    id: 6,
    question: "Je suis vétérinaire, comment rejoindre vetoRDV ?",
    answer: "Rendez-vous sur notre page 'Espace Vétérinaire' et créez votre compte professionnel. Notre équipe validera votre inscription sous 48h. L'abonnement est de 35€/mois pour 100 rendez-vous.",
    order: 6,
    active: true
  },
  {
    id: 7,
    question: "Quels types d'animaux sont acceptés ?",
    answer: "vetoRDV référence des vétérinaires pour tous types d'animaux : chiens, chats, NAC (lapins, furets, rongeurs, reptiles...), équins et animaux d'élevage. Utilisez le filtre 'Par animal' pour trouver un spécialiste.",
    order: 7,
    active: true
  },
  {
    id: 8,
    question: "Comment trouver un vétérinaire de garde ?",
    answer: "Utilisez notre fonction de géolocalisation et filtrez par 'Urgences'. Les vétérinaires de garde ou ouverts en dehors des heures habituelles seront mis en avant.",
    order: 8,
    active: true
  }
];

export const FAQProvider = ({ children }) => {
  const [faqs, setFaqs] = useState(() => {
    const saved = localStorage.getItem('vetordv_faqs');
    return saved ? JSON.parse(saved) : defaultFAQs;
  });

  useEffect(() => {
    localStorage.setItem('vetordv_faqs', JSON.stringify(faqs));
  }, [faqs]);

  const addFAQ = (faq) => {
    const newFaq = {
      ...faq,
      id: Date.now(),
      order: faqs.length + 1,
      active: true
    };
    setFaqs(prev => [...prev, newFaq]);
    return newFaq;
  };

  const updateFAQ = (id, updates) => {
    setFaqs(prev => prev.map(faq => 
      faq.id === id ? { ...faq, ...updates } : faq
    ));
  };

  const deleteFAQ = (id) => {
    setFaqs(prev => prev.filter(faq => faq.id !== id));
  };

  const reorderFAQs = (newOrder) => {
    setFaqs(newOrder);
  };

  const toggleFAQActive = (id) => {
    setFaqs(prev => prev.map(faq => 
      faq.id === id ? { ...faq, active: !faq.active } : faq
    ));
  };

  const getActiveFAQs = () => {
    return faqs.filter(faq => faq.active).sort((a, b) => a.order - b.order);
  };

  const resetToDefault = () => {
    setFaqs(defaultFAQs);
  };

  return (
    <FAQContext.Provider value={{
      faqs,
      addFAQ,
      updateFAQ,
      deleteFAQ,
      reorderFAQs,
      toggleFAQActive,
      getActiveFAQs,
      resetToDefault
    }}>
      {children}
    </FAQContext.Provider>
  );
};

export default FAQProvider;
